/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { type MRT_ColumnDef } from 'material-react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { usePostRequest } from '../../../../../../api/helper';
import VAR_COMMON_CONSTANTS from "../../../../../../constants/globalVariables";
import { stringToDateToLocaleString } from "../../../../../../helpers/helpers";
import { hasThisAccessLevelToRouteKey, } from "../../../../../../redux/ducks/AccessControl/accessControlHelpers";
import { selectCurrentUser } from "../../../../../../redux/ducks/AccessControl/slice";
import { EUserPermissionAccess } from "../../../../../../redux/ducks/AccessControl/types";
import {
  RedlineDatasetIdToData,
  selectDatasetsExecutionStatus,
  selectDatasetsLocalState,
  selectRedlineDatasetIdsToData,
  selectStudyIdToTreeTypeToTree,
  setRedlineDatasetIdsToData
} from "../../../../../../redux/ducks/CoverageRequirements/slice";
import { useAppSelector } from "../../../../../../redux/hooks";
import { EArgosRouteKeys } from "../../../../../../Routes/constants";
import ArgosFlexbox from '../../../../../components/Containers/ArgosFlexbox';
import { COVERAGE_REQUIREMENTS_CONSTANTS, coverageRequirementsDetails, usersDataEndpoint } from '../../../constants';
import CovAppReqTable from '../../../Table/CovAppReqTable';
import { getStudyNameById, processRedlineData } from '../../../utils';
import {
  COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_NAME_URL_PARAM,
  COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM,
  COVERAGE_REQUIREMENTS_STUDY_ID_URL_PARAM,
  EDatasetExecutionStatus,
  MERGED_DATASET_TREE_TYPE,
  SELECTED_DATASET_ID_URL_PARAM,
  SELECTED_DATASET_NAME_URL_PARAM,
  SELECTED_DATASET_TYPE_URL_PARAM,
  SINGLE_DATASET_TREE_TYPE
} from '../../constants';
import { renderLink } from '../CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties';
import { DashboardProps, DashboardRow } from './constants';

/**
 * ExecutionStatus is a tab inside Coverage App Requirements that displays dataset status, start time,
 * and other run details in a table.
 * 
 * @component
 * @param {DashboardProps} props
 * @returns {JSX.Element} 
 */
const ExecutionStatus = (props: DashboardProps) => {
  const dispatch = useDispatch();

  const redlineDatasetsFetchHandle = usePostRequest(COVERAGE_REQUIREMENTS_CONSTANTS.details.endpoints.fetchRedlineDatasetsForStudy)
  const usersFetchHandle = usePostRequest(usersDataEndpoint)

  const [userData, setUserData] = useState(null) // map gmid to name
  const [userIdNameMap, setUserIdNameMap] = useState<any>(null)
  const [dashboardTableRows, setDashboardTableRows] = useState<DashboardRow[]>([])
  const [isFetching, setIsFetching] = useState(true)

  const redlineDatasetIdsToData = useAppSelector(selectRedlineDatasetIdsToData)
  const datasetExecutionStatusMap: {
    [id: string]: EDatasetExecutionStatus
  } = useAppSelector((state) => selectDatasetsExecutionStatus(state))
  const datasetsLocalState = useAppSelector(selectDatasetsLocalState)

  // Trees for dataset path. TODO: If no studyId, we need to fetch all the trees, thus have a map of studyId to tree data (merged and single).
  const rawRedlineTreeDbItem = useAppSelector((state) => selectStudyIdToTreeTypeToTree(state, SINGLE_DATASET_TREE_TYPE, props.studyId))
  const mergedRedlineTreeDbItem = useAppSelector((state) => selectStudyIdToTreeTypeToTree(state, MERGED_DATASET_TREE_TYPE, props.studyId))

  const fetchUsersData = useCallback(async () => {
    // Get users data to render GMIDs as name strings for table
    setIsFetching(true)
    usersFetchHandle().then((data) => {
      const usersData = data.filter(
        (element: any) =>
          hasThisAccessLevelToRouteKey(EArgosRouteKeys.COVERAGE_REQUIREMENTS, element, EUserPermissionAccess.READ_WRITE)
      )
      const userIdNamesObj = usersData.map((elem: { id: any, firstName: string, lastName: string }) => {
        return {
          [elem.id]: elem.firstName + " " + elem.lastName,
        }
      })
      const userIdNames = Object.assign({}, ...userIdNamesObj)
      setUserData(usersData)
      setUserIdNameMap(userIdNames)
    }).catch((err) => {
      console.log(err)
      toast.error("Failed to load users data")
    }).finally(() => {
      setIsFetching(false)
    })
  }, [])

  const fetchRedlineData = useCallback(async () => {
    if(props.isLandingPage) return
    // TODO: If dont pass the studyId, then it will fetch all the redline datasets. Or, always fetch all, but filter out study id in other place.
    // Set map of redline dataset ids to data
    let body: any = {}
    if (props.studyId) {
      body = {
        studyId: props.studyId,
      }
    }
    setIsFetching(true)
    redlineDatasetsFetchHandle(body).then((_redlineData: RedlineDatasetIdToData) => {
      dispatch(setRedlineDatasetIdsToData({actionType: 'replaceAll', data: _redlineData}))
    }).catch((err) => {
      console.log(err)
      toast.error("Failed to load new redline data for the current study")
    }).finally(() => {
      setIsFetching(false)
    })
  }, [props.studyId])

  const gmid = useAppSelector(state => selectCurrentUser(state).id)

  // Set table rows
  useEffect(() => {
    const _rows = processRedlineData(
      props.studyId ?? null,
      redlineDatasetIdsToData,
      rawRedlineTreeDbItem,
      mergedRedlineTreeDbItem,
      datasetExecutionStatusMap,
      datasetsLocalState,
      dashboardTableRows,
      undefined,
      gmid,
      props.isLandingPage,
      userIdNameMap
  )
    if (_rows == null) return
    setDashboardTableRows(_rows)
  }, [redlineDatasetIdsToData, userIdNameMap, datasetExecutionStatusMap, datasetsLocalState, rawRedlineTreeDbItem, mergedRedlineTreeDbItem, props.studyId, props.onlyShowInProgressDatasets, props.isLandingPage, gmid])

  // Fetch initial data
  useEffect(() => {
    fetchUsersData()
    fetchRedlineData()
  }, [props.studyId, props.isLandingPage]); // TODO: This might be fine if studyId is optional. We can fetch all redline datasets if studyId is not passed, and we fetch all user data anyways.

  const cols = useMemo<MRT_ColumnDef<DashboardRow>[]>(
    () => [
      {
        id: 'studyId',
        header: 'Study',
        Cell: ({row}) => {
          return getStudyNameById(props.studyIdToName, row.original.studyId) ?? '';
        },
        // Group by studyId, but display studyName
        accessorFn: (row) => getStudyNameById(props.studyIdToName, row.studyId) ?? '',
        minSize: 200
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 100
      },
      {
        accessorKey: "path",
        header: VAR_COMMON_CONSTANTS.VAR_DATA_SET,
        Cell: ({row}) => {
          const name = row?.original?.name
          const displayName = row?.original?.outOfDate === true ? name + "*" : name
          const path = row?.original?.path
          const id = row?.original?.id
          const type = row?.original?.type
          const _tabName = type === "Single" ? "Raw " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS : "Merged " + VAR_COMMON_CONSTANTS.VAR_DATA_SETS
          const studyId = props.studyId ? props.studyId : row?.original?.studyId
          return (
            <ArgosFlexbox>
              {
                renderLink(
                  path,
                  `${coverageRequirementsDetails}`
                  + `?${SELECTED_DATASET_ID_URL_PARAM}=${id}`
                  + `&${SELECTED_DATASET_NAME_URL_PARAM}=${name}`
                  + `&${SELECTED_DATASET_TYPE_URL_PARAM}=${type}`
                  + `&${COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_ONE_NAME_URL_PARAM}=Coverage Analysis`
                  + `&${COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM}=${_tabName}`
                  + `&${COVERAGE_REQUIREMENTS_STUDY_ID_URL_PARAM}=${studyId}`
                 )
              }
            </ArgosFlexbox>
          )
        },
        sortingFn: (rowA: any, rowB: any, columnId: any): number => props.sortFu(rowA.getValue(columnId),rowB.getValue(columnId)),
        minSize: props.isLandingPage ? 300: 400
      },
      {
        accessorKey: 'status',
        header: 'Status',
      },
      {
        id: "progress",
        header: "Progress",
        accessorFn: (row) => {
          return row.status !== EDatasetExecutionStatus.IN_PROGRESS ? '' : row.progress?.length > 0 ? row.progress + '%' : ''
        },
      },
      {
        id: "dateGenerated",
        header: "Start Time",
        accessorFn: (row) => {
          return row.dateGenerated ? stringToDateToLocaleString(row.dateGenerated, true) ?? '' : ''
        },
      },
      {
        id: "endTime",
        header: "End Time",
        accessorFn: (row) => {
          return row.endTime ? stringToDateToLocaleString(row.endTime, true) ?? '' : ''
        },
      },
      {
        accessorKey: "runTime",
        header: "Run Time",
      },
      {
        accessorKey: "generator",
        header: "Executed By",

      }
    ],
    [userIdNameMap, props.studyId, props.studyIdToName, props.isLandingPage]
  )

  return (<CovAppReqTable
      isLoadingInitialData={props.isLoadingInitialData ?? false}
      columns={cols}
      data={dashboardTableRows}
      isLandingPage={props.isLandingPage ?? false}
      isFetching={isFetching}
      studyId={props.studyId ?? ""}
      studyIds={props.studyIds ?? []}
      onlyShowInProgressDatasets={props.onlyShowInProgressDatasets}
    />
  );
}


export default React.memo(ExecutionStatus)
